import { z } from 'zod';
import { Prisma } from '@prisma/client';

/////////////////////////////////////////
// HELPER FUNCTIONS
/////////////////////////////////////////

// JSON
//------------------------------------------------------

export type NullableJsonInput = Prisma.JsonValue | null | 'JsonNull' | 'DbNull' | Prisma.NullTypes.DbNull | Prisma.NullTypes.JsonNull;

export const transformJsonNull = (v?: NullableJsonInput) => {
  if (!v || v === 'DbNull') return Prisma.DbNull;
  if (v === 'JsonNull') return Prisma.JsonNull;
  return v;
};

export const JsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
  ])
);

export type JsonValueType = z.infer<typeof JsonValueSchema>;

export const NullableJsonValue = z
  .union([JsonValueSchema, z.literal('DbNull'), z.literal('JsonNull')])
  .nullable()
  .transform((v) => transformJsonNull(v));

export type NullableJsonValueType = z.infer<typeof NullableJsonValue>;

export const InputJsonValueSchema: z.ZodType<Prisma.InputJsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: z.function(z.tuple([]), z.any()) }),
    z.record(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
  ])
);

export type InputJsonValueType = z.infer<typeof InputJsonValueSchema>;


/////////////////////////////////////////
// ENUMS
/////////////////////////////////////////

export const TransactionIsolationLevelSchema = z.enum(['ReadUncommitted','ReadCommitted','RepeatableRead','Serializable']);

export const ApiTokenScalarFieldEnumSchema = z.enum(['id','name','key','encryptedToken','createdAt','updatedAt','ownerId']);

export const CreditTransactionScalarFieldEnumSchema = z.enum(['id','type','amount','createdAt','organizationId','searchId']);

export const SearchQueueScalarFieldEnumSchema = z.enum(['id','targetSmiles','searchParams','priority','isRunning','createdAt','updatedAt','source','retryCount','order','canonizedSmiles','batchId','startingMaterialsId','organizationName']);

export const SearchRequestScalarFieldEnumSchema = z.enum(['id','name','size','inputParams','searchParams','startingMaterialsSize','priority','isRemoved','createdAt','source','metadata','presetParams','presetName','ownerId','startingMaterialsId','executionMode']);

export const SearchResultScalarFieldEnumSchema = z.enum(['id','order','targetSmiles','canonizedSmiles','targetMolfile','status','result','reactionCount','referenceCount','certainty','price','synthesis','searchGraph','decomposition','timedOut','metadata','startedAt','finishedAt','sciFinderId','batchId','hypertreeS3Path','searchGraphS3Path','searchConfigS3Path']);

export const SearchStartingMaterialsScalarFieldEnumSchema = z.enum(['id','userProvidedSmiles','smiles','createdAt','updatedAt']);

export const SearchTargetMetadataScalarFieldEnumSchema = z.enum(['id','name','createdAt']);

export const SharedSearchScalarFieldEnumSchema = z.enum(['searchRequestId','userId']);

export const DocumentAcceptanceScalarFieldEnumSchema = z.enum(['id','name','version','createdAt','updatedAt','ownerId']);

export const RateLimitScalarFieldEnumSchema = z.enum(['key','points','expire']);

export const ReactionSearchPreferenceScalarFieldEnumSchema = z.enum(['id','searchParams','name','isWebDefault','isApiDefault','createdAt','updatedAt','ownerId']);

export const ReportScalarFieldEnumSchema = z.enum(['id','title','description','createdAt','updatedAt','ownerId']);

export const ReportItemScalarFieldEnumSchema = z.enum(['id','type','payload','ordinal','createdAt','updatedAt','reportId']);

export const StartingMaterialDatasetScalarFieldEnumSchema = z.enum(['id','name','description','fileName','size','createdAt','uploadedAt','ownerId']);

export const UserScalarFieldEnumSchema = z.enum(['id','email','activeAt','createdAt','updatedAt','cognitoSub','organizationId']);

export const OrganizationScalarFieldEnumSchema = z.enum(['id','name','creditBalance','creditCycle','creditsPerCycle','creditCycleNextCreditAt','createdAt','updatedAt']);

export const SortOrderSchema = z.enum(['asc','desc']);

export const NullableJsonNullValueInputSchema = z.enum(['DbNull','JsonNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.DbNull : value);

export const JsonNullValueInputSchema = z.enum(['JsonNull',]).transform((value) => (value === 'JsonNull' ? Prisma.JsonNull : value));

export const QueryModeSchema = z.enum(['default','insensitive']);

export const NullsOrderSchema = z.enum(['first','last']);

export const JsonNullValueFilterSchema = z.enum(['DbNull','JsonNull','AnyNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.JsonNull : value === 'AnyNull' ? Prisma.AnyNull : value);
/////////////////////////////////////////
// MODELS
/////////////////////////////////////////

/////////////////////////////////////////
// API TOKEN SCHEMA
/////////////////////////////////////////

export const ApiTokenSchema = z.object({
  id: z.string(),
  name: z.string(),
  key: z.string(),
  encryptedToken: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  ownerId: z.string(),
})

export type ApiToken = z.infer<typeof ApiTokenSchema>

// API TOKEN OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const ApiTokenOptionalDefaultsSchema = ApiTokenSchema.merge(z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type ApiTokenOptionalDefaults = z.infer<typeof ApiTokenOptionalDefaultsSchema>

/////////////////////////////////////////
// CREDIT TRANSACTION SCHEMA
/////////////////////////////////////////

export const CreditTransactionSchema = z.object({
  id: z.string(),
  type: z.string(),
  amount: z.bigint(),
  createdAt: z.coerce.date(),
  organizationId: z.string(),
  searchId: z.string().nullable(),
})

export type CreditTransaction = z.infer<typeof CreditTransactionSchema>

// CREDIT TRANSACTION OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const CreditTransactionOptionalDefaultsSchema = CreditTransactionSchema.merge(z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
}))

export type CreditTransactionOptionalDefaults = z.infer<typeof CreditTransactionOptionalDefaultsSchema>

/////////////////////////////////////////
// SEARCH QUEUE SCHEMA
/////////////////////////////////////////

export const SearchQueueSchema = z.object({
  id: z.string(),
  targetSmiles: z.string(),
  searchParams: JsonValueSchema,
  priority: z.number().int(),
  isRunning: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  source: z.string(),
  retryCount: z.number().int(),
  order: z.number().int(),
  canonizedSmiles: z.string().nullable(),
  batchId: z.string(),
  startingMaterialsId: z.string().nullable(),
  organizationName: z.string(),
})

export type SearchQueue = z.infer<typeof SearchQueueSchema>

// SEARCH QUEUE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const SearchQueueOptionalDefaultsSchema = SearchQueueSchema.merge(z.object({
  id: z.string().optional(),
  searchParams: JsonValueSchema,
  priority: z.number().int().optional(),
  isRunning: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  retryCount: z.number().int().optional(),
  order: z.number().int().optional(),
}))

export type SearchQueueOptionalDefaults = z.infer<typeof SearchQueueOptionalDefaultsSchema>

/////////////////////////////////////////
// SEARCH REQUEST SCHEMA
/////////////////////////////////////////

export const SearchRequestSchema = z.object({
  id: z.string(),
  name: z.string(),
  size: z.number().int(),
  inputParams: JsonValueSchema,
  searchParams: JsonValueSchema,
  startingMaterialsSize: z.number().int(),
  priority: z.number().int(),
  isRemoved: z.boolean(),
  createdAt: z.coerce.date(),
  source: z.string(),
  metadata: JsonValueSchema.nullable(),
  presetParams: JsonValueSchema,
  presetName: z.string().nullable(),
  ownerId: z.string(),
  startingMaterialsId: z.string().nullable(),
  executionMode: z.string(),
})

export type SearchRequest = z.infer<typeof SearchRequestSchema>

// SEARCH REQUEST OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const SearchRequestOptionalDefaultsSchema = SearchRequestSchema.merge(z.object({
  id: z.string().optional(),
  startingMaterialsSize: z.number().int().optional(),
  priority: z.number().int().optional(),
  isRemoved: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  metadata: JsonValueSchema.nullable(),
  executionMode: z.string().optional(),
}))

export type SearchRequestOptionalDefaults = z.infer<typeof SearchRequestOptionalDefaultsSchema>

/////////////////////////////////////////
// SEARCH RESULT SCHEMA
/////////////////////////////////////////

export const SearchResultSchema = z.object({
  id: z.string(),
  order: z.number().int(),
  targetSmiles: z.string(),
  canonizedSmiles: z.string().nullable(),
  targetMolfile: z.string().nullable(),
  status: z.string(),
  result: z.number().nullable(),
  reactionCount: z.number().int().nullable(),
  referenceCount: z.number().int().nullable(),
  certainty: z.number().nullable(),
  price: z.number().nullable(),
  synthesis: JsonValueSchema,
  searchGraph: z.string().nullable(),
  decomposition: JsonValueSchema,
  timedOut: z.boolean().nullable(),
  metadata: JsonValueSchema.nullable(),
  startedAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  sciFinderId: z.string().nullable(),
  batchId: z.string(),
  hypertreeS3Path: z.string().nullable(),
  searchGraphS3Path: z.string().nullable(),
  searchConfigS3Path: z.string().nullable(),
})

export type SearchResult = z.infer<typeof SearchResultSchema>

// SEARCH RESULT OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const SearchResultOptionalDefaultsSchema = SearchResultSchema.merge(z.object({
  id: z.string().optional(),
  order: z.number().int().optional(),
  metadata: JsonValueSchema.nullable(),
  startedAt: z.coerce.date().optional(),
}))

export type SearchResultOptionalDefaults = z.infer<typeof SearchResultOptionalDefaultsSchema>

/////////////////////////////////////////
// SEARCH STARTING MATERIALS SCHEMA
/////////////////////////////////////////

export const SearchStartingMaterialsSchema = z.object({
  id: z.string(),
  userProvidedSmiles: z.string().array(),
  smiles: z.string().array(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type SearchStartingMaterials = z.infer<typeof SearchStartingMaterialsSchema>

// SEARCH STARTING MATERIALS OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const SearchStartingMaterialsOptionalDefaultsSchema = SearchStartingMaterialsSchema.merge(z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type SearchStartingMaterialsOptionalDefaults = z.infer<typeof SearchStartingMaterialsOptionalDefaultsSchema>

/////////////////////////////////////////
// SEARCH TARGET METADATA SCHEMA
/////////////////////////////////////////

export const SearchTargetMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.coerce.date(),
})

export type SearchTargetMetadata = z.infer<typeof SearchTargetMetadataSchema>

// SEARCH TARGET METADATA OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const SearchTargetMetadataOptionalDefaultsSchema = SearchTargetMetadataSchema.merge(z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
}))

export type SearchTargetMetadataOptionalDefaults = z.infer<typeof SearchTargetMetadataOptionalDefaultsSchema>

/////////////////////////////////////////
// SHARED SEARCH SCHEMA
/////////////////////////////////////////

export const SharedSearchSchema = z.object({
  searchRequestId: z.string(),
  userId: z.string(),
})

export type SharedSearch = z.infer<typeof SharedSearchSchema>

// SHARED SEARCH OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const SharedSearchOptionalDefaultsSchema = SharedSearchSchema.merge(z.object({
}))

export type SharedSearchOptionalDefaults = z.infer<typeof SharedSearchOptionalDefaultsSchema>

/////////////////////////////////////////
// DOCUMENT ACCEPTANCE SCHEMA
/////////////////////////////////////////

export const DocumentAcceptanceSchema = z.object({
  id: z.string(),
  name: z.string(),
  version: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  ownerId: z.string(),
})

export type DocumentAcceptance = z.infer<typeof DocumentAcceptanceSchema>

// DOCUMENT ACCEPTANCE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const DocumentAcceptanceOptionalDefaultsSchema = DocumentAcceptanceSchema.merge(z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type DocumentAcceptanceOptionalDefaults = z.infer<typeof DocumentAcceptanceOptionalDefaultsSchema>

/////////////////////////////////////////
// RATE LIMIT SCHEMA
/////////////////////////////////////////

export const RateLimitSchema = z.object({
  key: z.string(),
  points: z.number().int(),
  expire: z.bigint().nullable(),
})

export type RateLimit = z.infer<typeof RateLimitSchema>

// RATE LIMIT OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const RateLimitOptionalDefaultsSchema = RateLimitSchema.merge(z.object({
  points: z.number().int().optional(),
}))

export type RateLimitOptionalDefaults = z.infer<typeof RateLimitOptionalDefaultsSchema>

/////////////////////////////////////////
// REACTION SEARCH PREFERENCE SCHEMA
/////////////////////////////////////////

export const ReactionSearchPreferenceSchema = z.object({
  id: z.string(),
  searchParams: JsonValueSchema.nullable(),
  name: z.string().trim(),
  isWebDefault: z.boolean(),
  isApiDefault: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  ownerId: z.string(),
})

export type ReactionSearchPreference = z.infer<typeof ReactionSearchPreferenceSchema>

// REACTION SEARCH PREFERENCE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const ReactionSearchPreferenceOptionalDefaultsSchema = ReactionSearchPreferenceSchema.merge(z.object({
  id: z.string().optional(),
  isWebDefault: z.boolean().optional(),
  isApiDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type ReactionSearchPreferenceOptionalDefaults = z.infer<typeof ReactionSearchPreferenceOptionalDefaultsSchema>

/////////////////////////////////////////
// REPORT SCHEMA
/////////////////////////////////////////

export const ReportSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  ownerId: z.string(),
})

export type Report = z.infer<typeof ReportSchema>

// REPORT OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const ReportOptionalDefaultsSchema = ReportSchema.merge(z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type ReportOptionalDefaults = z.infer<typeof ReportOptionalDefaultsSchema>

/////////////////////////////////////////
// REPORT ITEM SCHEMA
/////////////////////////////////////////

export const ReportItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  payload: JsonValueSchema.nullable(),
  ordinal: z.number().int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  reportId: z.string(),
})

export type ReportItem = z.infer<typeof ReportItemSchema>

// REPORT ITEM OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const ReportItemOptionalDefaultsSchema = ReportItemSchema.merge(z.object({
  id: z.string().optional(),
  payload: JsonValueSchema.nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type ReportItemOptionalDefaults = z.infer<typeof ReportItemOptionalDefaultsSchema>

/////////////////////////////////////////
// STARTING MATERIAL DATASET SCHEMA
/////////////////////////////////////////

export const StartingMaterialDatasetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  fileName: z.string(),
  size: z.bigint(),
  createdAt: z.coerce.date(),
  uploadedAt: z.coerce.date().nullable(),
  ownerId: z.string(),
})

export type StartingMaterialDataset = z.infer<typeof StartingMaterialDatasetSchema>

// STARTING MATERIAL DATASET OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const StartingMaterialDatasetOptionalDefaultsSchema = StartingMaterialDatasetSchema.merge(z.object({
  id: z.string().optional(),
  description: z.string().optional(),
  createdAt: z.coerce.date().optional(),
}))

export type StartingMaterialDatasetOptionalDefaults = z.infer<typeof StartingMaterialDatasetOptionalDefaultsSchema>

/////////////////////////////////////////
// USER SCHEMA
/////////////////////////////////////////

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  activeAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  cognitoSub: z.string().nullable(),
  organizationId: z.string(),
})

export type User = z.infer<typeof UserSchema>

// USER OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const UserOptionalDefaultsSchema = UserSchema.merge(z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type UserOptionalDefaults = z.infer<typeof UserOptionalDefaultsSchema>

/////////////////////////////////////////
// ORGANIZATION SCHEMA
/////////////////////////////////////////

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  creditBalance: z.bigint(),
  creditCycle: z.string(),
  creditsPerCycle: z.bigint(),
  creditCycleNextCreditAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Organization = z.infer<typeof OrganizationSchema>

// ORGANIZATION OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------

export const OrganizationOptionalDefaultsSchema = OrganizationSchema.merge(z.object({
  id: z.string().optional(),
  creditBalance: z.bigint().optional(),
  creditCycle: z.string().optional(),
  creditsPerCycle: z.bigint().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}))

export type OrganizationOptionalDefaults = z.infer<typeof OrganizationOptionalDefaultsSchema>
